import { ApiError, apiClient } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import type {
  BillingCheckoutRead,
  BillingConfigRead,
  BillingPlanChoice,
  BillingPortalRead,
  BillingSubscriptionRead,
  BillingSubscriptionStatus,
} from '../../types/api';

const BILLING_PATH = '/api/v1/billing';

/** What the SPA reads when billing is off or its config could not be read. */
export const BILLING_OFF: BillingConfigRead = {
  enabled: false,
  environment: null,
  client_token: null,
};

/** GET /billing/config: whether billing is on, and Paddle's public client token. */
export function fetchBillingConfig(signal?: AbortSignal): Promise<BillingConfigRead> {
  return apiClient.get<BillingConfigRead>(`${BILLING_PATH}/config`, { signal });
}

/** GET /billing/subscription: the account's subscription, `status: 'none'` without one. */
export function fetchSubscription(signal?: AbortSignal): Promise<BillingSubscriptionRead> {
  return apiClient.get<BillingSubscriptionRead>(`${BILLING_PATH}/subscription`, { signal });
}

/**
 * POST /billing/checkout: a Paddle transaction for this plan and interval,
 * bound server-side to the signed-in account. 409 `ALREADY_SUBSCRIBED` with a
 * live subscription (switch through change-plan instead).
 */
export function startCheckout(body: BillingPlanChoice): Promise<BillingCheckoutRead> {
  return apiClient.post<BillingCheckoutRead>(`${BILLING_PATH}/checkout`, { body });
}

/**
 * POST /billing/change-plan: switches the live subscription, prorated now.
 * 409 `NO_SUBSCRIPTION`, 409 `PLAN_UNCHANGED`.
 */
export function changePlan(body: BillingPlanChoice): Promise<BillingSubscriptionRead> {
  return apiClient.post<BillingSubscriptionRead>(`${BILLING_PATH}/change-plan`, { body });
}

/** POST /billing/cancel: cancels at the end of the period paid for. */
export function cancelSubscription(): Promise<BillingSubscriptionRead> {
  return apiClient.post<BillingSubscriptionRead>(`${BILLING_PATH}/cancel`);
}

/** POST /billing/resume: takes back a scheduled cancel. */
export function resumeSubscription(): Promise<BillingSubscriptionRead> {
  return apiClient.post<BillingSubscriptionRead>(`${BILLING_PATH}/resume`);
}

/** POST /billing/portal: a Paddle portal link (card, invoices, receipts). */
export function fetchPortalUrl(): Promise<BillingPortalRead> {
  return apiClient.post<BillingPortalRead>(`${BILLING_PATH}/portal`);
}

/** The statuses that hold a plan: checkout refuses, change-plan works. */
export function isLiveSubscription(status: BillingSubscriptionStatus | undefined): boolean {
  return status === 'active' || status === 'trialing' || status === 'past_due';
}

/**
 * Opens the Paddle portal in a new tab. The tab opens inside the click, before
 * the link is minted, so popup blockers let it through.
 */
export async function openPortal(): Promise<void> {
  const tab = window.open('about:blank', '_blank');
  try {
    const { url } = await fetchPortalUrl();
    if (tab) {
      tab.opener = null;
      tab.location.href = url;
    } else {
      window.location.assign(url);
    }
  } catch (caught) {
    tab?.close();
    throw caught;
  }
}

/** Every billing error code in the user's words. */
const BILLING_ERROR_MESSAGE: Readonly<Record<string, string>> = {
  BILLING_DISABLED: 'Billing is not open yet.',
  ALREADY_SUBSCRIBED: 'This account already has a subscription. Switch plans instead.',
  NO_SUBSCRIPTION: 'This account has no active subscription.',
  PLAN_UNCHANGED: 'You are already on that plan.',
  BILLING_PROVIDER_ERROR: 'The payment provider did not answer. Try again in a minute.',
  BILLING_SIGNATURE_INVALID: 'A billing notice could not be verified.',
  BILLING_SUBSCRIPTION_ACTIVE:
    'This account pays through Paddle. Cancel the subscription in Paddle first, or start the note with "pin:" to override it.',
};

/** The message for a failed billing call: the code's words, or the usual message. */
export function billingErrorMessage(caught: unknown, fallback: string): string {
  const message =
    caught instanceof ApiError && caught.code ? BILLING_ERROR_MESSAGE[caught.code] : undefined;
  return message ?? getErrorMessage(caught, fallback);
}
